import React, { useEffect, useState } from 'react';
import { 
  Box, Typography, Paper, IconButton, Tooltip, Chip, 
  Button, Breadcrumbs, Link, CircularProgress,
  ThemeProvider,
  createTheme
} from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { useNotify } from '../../../components/Datatable/NotifyProvider';
import { useAuth } from '../../../components/AuthProvider';
import { useLoading } from '../../../components/LoadingProvider';
import api from '../../../services/api';
import Datatable from '../../../components/Datatable';

// Icons
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import RemoveCircleOutline from '@mui/icons-material/RemoveCircleOutline';
import SyncIcon from '@mui/icons-material/Sync';

// Components
import CategoryForm from './CategoryForm';
import DomainForm from './DomainForm';
import SyncDNSButton from './SyncDNSButton';
import { ToggleOff, ToggleOn } from '@mui/icons-material';
import Swal from 'sweetalert2';

export default function CategoryDetail() {
  const { categoryId } = useParams();
  const navigate = useNavigate();
  const notify = useNotify();
  const auth = useAuth();
  const loading = useLoading();
  const { isLoading, setLoading } = loading;
  
  const [category, setCategory] = useState(null);
  const [categories, setCategories] = useState([]);
  const [refresh, setRefresh] = useState(false);
  const [domainFormState, setDomainFormState] = useState({
    open: false,
    id: null
  });
  const [categoryFormState, setCategoryFormState] = useState({
    open: false,
    id: null,
    category: null
  });
  
  // Carregar detalhes da categoria
  const fetchCategoryDetails = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/dns/blacklist/categories/${categoryId}`);

      if (response.data && !response.data.error) {

        setCategory(response.data);
      } else if (response.data.error) {
        notify.setNotify({
          open: true,
          message: response.data.error,
          severity: 'error'
        });
        //navigate('/dns/blacklist'); // Redirecionar se a categoria não for encontrada
      }
    } catch (error) {
      notify.setNotify({
        open: true,
        message: error.response?.data?.error || 'Erro ao carregar detalhes da categoria',
        severity: 'error'
      });
      navigate('/dns/blacklist');
    } finally {
      setLoading(false);
    }
  };

  // Carregar todas as categorias (para o formulário de domínios)
  const fetchCategories = async () => {
    try {
      const response = await api.get('/dns/blacklist/categories/all');
      if (response.data) {
        setCategories(response.data || []);
      }
    } catch (error) {
      console.error("Erro ao carregar categorias:", error);
    }
  };

  // Carregar dados ao iniciar
  useEffect(() => {
    fetchCategoryDetails();
    fetchCategories();
  }, [categoryId, refresh]);

  // Função para ativar/desativar categoria
  const toggleCategoryActive = async () => {
    if (!category) return;
    
    try {
      setLoading(true);
      const response = await api.put(`/dns/blacklist/categories/${categoryId}`, {
        active: !category.active
      });
      
      if (response.data.id) {
        const newStatus = response.data.active;
        notify.setNotify({
          open: true,
          message: `Categoria ${newStatus ? 'ativada' : 'desativada'} com sucesso`,
          severity: 'success'
        });
        
        // Atualizar dados
        setRefresh(!refresh);
      } else if (response.data.error) {
        notify.setNotify({
          open: true,
          message: response.data.error,
          severity: 'error'
        });
      }
    } catch (error) {
      notify.setNotify({
        open: true,
        message: error.response?.data?.error || 'Erro ao atualizar status da categoria',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  // Função para editar categoria
  const handleEditCategory = () => {
    if (!category) return;
    
    setCategoryFormState({
      open: true,
      id: categoryId,
      category: category
    });
  };

  // Função para deletar categoria
  const handleDeleteCategory = async () => {
    if (!category) return;
    
    
    try {
      setLoading(true);
      const response = await api.delete(`/dns/blacklist/categories/${categoryId}`);
      
      if (response.data.success) {
        notify.setNotify({
          open: true,
          message: response.data.success,
          severity: 'success'
        });
        
        // Redirecionar para a lista de categorias
        navigate('/dns/blacklist');
      } else if (response.data.error) {
        notify.setNotify({
          open: true,
          message: response.data.error,
          severity: 'error'
        });
      }
    } catch (error) {
      notify.setNotify({
        open: true,
        message: error.response?.data?.error || 'Erro ao excluir categoria',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  // Função para atualizar categoria
  const handleSubmitCategory = async (categoryData) => {
    try {
      setLoading(true);
      const response = await api.put(`/dns/blacklist/categories/${categoryId}`, categoryData);
      
      if (response.data.id) {
        notify.setNotify({
          open: true,
          message: 'Categoria atualizada com sucesso',
          severity: 'success'
        });
        
        // Fechar o formulário
        setCategoryFormState({ open: false, id: null, category: null });
        
        // Atualizar dados
        setRefresh(!refresh);
      } else if (response.data.error) {
        notify.setNotify({
          open: true,
          message: response.data.error,
          severity: 'error'
        });
      }
    } catch (error) {
      notify.setNotify({
        open: true,
        message: error.response?.data?.error || 'Erro ao atualizar categoria',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  // Função para adicionar domínios
  const handleAddDomains = async (formData) => {
    //try {
      setLoading(true);
      
      // Verificar se temos algum conteúdo (texto ou arquivo)
      const hasBlacklist = formData.get('blacklist') && formData.get('blacklist').trim() !== '';
      const hasFile = formData.get('file') !== null;
      
      if (!hasBlacklist && !hasFile) {
        notify.setNotify({
          open: true,
          message: 'Nenhum domínio ou arquivo informado',
          severity: 'warning'
        });
        return;
      }
      
      // Enviar FormData diretamente
      const response = await api.post('/dns/blacklist/categories/add_domains', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      if (response.data.added_domains >= 0) {
        notify.setNotify({
          open: true,
          message: response.data.message || `${response.data.added_domains} domínios adicionados com sucesso`,
          severity: 'success'
        });
        
        // Fechar o formulário
        setDomainFormState({ open: false, id: null });
        
        // Atualizar dados
        setRefresh(!refresh);
      } else if (response.data.error) {
        notify.setNotify({
          open: true,
          message: response.data.error,
          severity: 'error'
        });
      }
    // } catch (error) {
    //   notify.setNotify({
    //     open: true,
    //     message: error.response?.data?.error || 'Erro ao adicionar domínios',
    //     severity: 'error'
    //   });
    // } finally {
      // }
         setLoading(false);
  };

  // Função para ativar/desativar domínio
  const toggleDomainActive = async (domainId, currentStatus) => {
    try {
      setLoading(true);
      const response = await api.put(`/dns/blacklist/domains/${domainId}`, {
        active: !currentStatus
      });
      
      if (response.data.success) {
        notify.setNotify({
          open: true,
          message: response.data.success,
          severity: 'success'
        });
        
        // Atualizar dados
        setRefresh(!refresh);
      } else if (response.data.error) {
        notify.setNotify({
          open: true,
          message: response.data.error,
          severity: 'error'
        });
      }
    } catch (error) {
      notify.setNotify({
        open: true,
        message: error.response?.data?.error || 'Erro ao atualizar status do domínio',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  // Função para excluir um domínio
  const deleteDomain = async (domain, category) => {
    try {
      setLoading(true);
      const response = await api.delete(`/dns/blacklist/categories/${category}/domains/${domain}`);
      
      if (response.data.nodes) {
        notify.setNotify({
          open: true,
          message: response.data.message || 'Domínio removido com sucesso',
          severity: 'success'
        });
        
        // Atualizar dados
        setRefresh(!refresh);
      } else if (response.data.error) {
        notify.setNotify({
          open: true,
          message: response.data.error,
          severity: 'error'
        });
      }
    } catch (error) {
      notify.setNotify({
        open: true,
        message: error.response?.data?.error || 'Erro ao excluir domínio',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  // Função para excluir múltiplos domínios
  const deleteMultipleDomains = async (domains) => {
    try {
      setLoading(true);
      const response = await api.post('/dns/blacklist/domains/delete', {
        blacklist: domains
      });
      
      if (response.data.nodes) {
        notify.setNotify({
          open: true,
          message: response.data.message || `${domains.length} domínios removidos com sucesso`,
          severity: 'success'
        });
        
        // Atualizar dados
        setRefresh(!refresh);
      } else if (response.data.error) {
        notify.setNotify({
          open: true,
          message: response.data.error,
          severity: 'error'
        });
      }
    } catch (error) {
      notify.setNotify({
        open: true,
        message: error.response?.data?.error || 'Erro ao excluir domínios',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  // Preparar dados para a tabela
  const tableData = [];  // Será preenchido pelo endpoint

  // Atualizar o título da página
  React.useEffect(() => {
    document.title = `IPV7 Tools - Categoria: ${category?.name || 'Carregando...'}`
  }, [category]);

  return (
     <ThemeProvider theme={createTheme({
                    typography:{
                        fontFamily:[
                          'sans-serif',
                          'Roboto',
                          'Montserrat',
                        ]
                      },
                      palette: {
                        mode: 'light',
                        primary: {
                          main: "#e01b29",
                        },
                        }
                })}>
      {/* Formulários */}
      <CategoryForm
        open={categoryFormState.open}
        category={categoryFormState.category}
        onClose={() => setCategoryFormState({ open: false, id: null, category: null })}
        onSubmit={handleSubmitCategory}
      />
      
      <DomainForm
        open={domainFormState.open}
        id={domainFormState.id}
        categories={categories?.data || []}
        onClose={() => setDomainFormState({ open: false, id: null })}
        onSubmit={handleAddDomains}
      />
      
      {/* Cabeçalho */}
      <Box sx={{ mb: 3 }}>
        <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
          <Link 
            color="inherit" 
            href="#" 
            onClick={(e) => { e.preventDefault(); navigate('/dns_blacklist'); }}
          >
            DNS Blacklist
          </Link>
          <Typography color="text.primary">
            Categoria: {category?.name || 'Carregando...'}
          </Typography>
        </Breadcrumbs>
        
        {isLoading && !category ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress />
          </Box>
        ) : category ? (
          <>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <IconButton 
                  color="primary" 
                  onClick={() => navigate('/dns_blacklist')}
                  sx={{ mr: 1 }}
                >
                  <ArrowBackIcon />
                </IconButton>
                <Typography variant="h5" component="h1">
                  {category.name}
                </Typography>
                <Chip 
                  label={category.active ? "Ativa" : "Inativa"}
                  color={category.active ? "success" : "default"}
                  sx={{ ml: 2 }}
                />
                {category.expired_at && (
                  <Chip 
                    label={`Expira: ${format(new Date(category.expired_at), 'dd/MM/yyyy')}`}
                    color="warning"
                    sx={{ ml: 1 }}
                  />
                )}
              </Box>
              
              <Box>
                {/* <SyncDNSButton 
                  onRefresh={() => setRefresh(!refresh)}
                /> */}
                
                <Button
                  variant="outlined"
                  startIcon={<EditIcon />}
                  disabled={!auth.hasPerm("dns_blacklist.update") || (category.expired && new Date(category.expired) < new Date()) }
                  onClick={handleEditCategory}
                  sx={{ ml: 1 }}
                >
                  Editar
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteIcon />}
                  disabled={!auth.hasPerm("dns_blacklist.delete") || (category.expired && new Date(category.expired) < new Date()) }
                  onClick={()=>{
                    Swal.fire({
                      title: 'Tem certeza?',
                      text: `Deseja excluir a categoria "${category.name}"? Os domínios serão removidos.`,
                      icon: 'warning',
                      showCancelButton: true,
                      confirmButtonText: 'Sim',
                      cancelButtonText: 'Não'
                    }).then((result) => {
                      if (result.isConfirmed) {
                          handleDeleteCategory()
                      }
                    }
                    );
                  }}
                  sx={{ ml: 1 }}
                >
                  Excluir
                </Button>
              </Box>
            </Box>
            
            {category.description && (
              <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                {category.description}
              </Typography>
            )}
            
           
          </>
        ) : (
          <Typography variant="h6" color="error">
            Categoria não encontrada
          </Typography>
        )}
      </Box>
      
      {/* Tabela de domínios */}
      {category && (
        <Paper elevation={3} sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">Domínios</Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              disabled={!auth.hasPerm("dns_blacklist.create") || (category.expired && new Date(category.expired) < new Date()) }
              onClick={() => setDomainFormState({ 
                open: true, 
                id: null,
                defaultCategory: categoryId
              })}
            >
              Adicionar Domínios
            </Button>
          </Box>
          
          <Datatable
            refresh={refresh}
            handlerRefresh={() => setRefresh(!refresh)}
            title=""
            endpoint={`/dns/blacklist/domains?category=${categoryId}`}
            options={{
              customToolbar: () =>
                <>
                  <Tooltip title="Remover todos os filtros">
                    <IconButton onClick={() => { localStorage.removeItem(`datatable/dns/blacklist/domains?category=${categoryId}`); window.location.reload() }} >
                      <RemoveCircleOutline />
                    </IconButton>
                  </Tooltip>
                  {/* <Tooltip title="Adicionar Domínios">
                    <IconButton 
                      disabled={!auth.hasPerm("dns_blacklist.create")}
                      onClick={() => setDomainFormState({ 
                        open: true, 
                        id: null 
                      })}
                    >
                      <AddIcon />
                    </IconButton>
                  </Tooltip> */}
                </>
              ,
              selectableRows: "multiple",
              customToolbarSelect: (selectedRows, displayData, setSelectedRows) => 
                <Tooltip title="Remover domínios selecionados">
                  <IconButton 
                    disabled={!auth.hasPerm('dns_blacklist.delete')}
                    onClick={() => {
                      const selectedDomains = selectedRows.data.map((row) => displayData[row.dataIndex].data[1]);
                      
                      if (window.confirm('Deseja remover os domínios selecionados?')) {
                        deleteMultipleDomains(selectedDomains);
                        setSelectedRows([]);
                      }
                    }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Tooltip>
            }}
            columns={[
              {
                name: 'id',
                label: 'ID',
                options: {
                  display: false
                }
              },
              {
                name: 'domain',
                label: 'Domínio',
              },
              // {
              //   name: 'status',
              //   label: 'Status',
              //   options: {
              //     customBodyRender: (value) => (
              //       <Chip 
              //         label={value} 
              //         size="small"
              //         color={value === 'Ativo' ? 'success' : 'default'}
              //       />
              //     )
              //   }
              // },
              {
                name: 'active',
                label: 'Ativo',
                options: {
                  display: false
                }
              },
              {
                name: 'actions',
                label: 'Ações',
                options: {
                  filter: false,
                  sort: false,
                  customBodyRender: (value, tableMeta) => {
                    const domainId = tableMeta.rowData[0];
                    const domain = tableMeta.rowData[1];
                    const isActive = tableMeta.rowData[3];
                    
                    return (
                      <Box>
                       
                        <Tooltip title="Excluir">
                          <IconButton
                            size="small"
                            disabled={!auth.hasPerm("dns_blacklist.delete") || (category.expired && new Date(category.expired) < new Date()) }
                            onClick={() => {
                              Swal.fire({
                                title: 'Tem certeza?',
                                text: `Deseja excluir o domínio "${domain}"?`,
                                icon: 'warning',
                                showCancelButton: true,
                                confirmButtonText: 'Sim',
                                cancelButtonText: 'Não'
                              }).then((result) => {
                                if (result.isConfirmed) {
                                  deleteDomain(domainId, categoryId);
                                }
                              });
                              // if (window.confirm('Deseja excluir o domínio?')) {
                              //   deleteDomain(domain);
                              
                            }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    );
                  }
                }
              }
            ]}
          />
        </Paper>
      )}
    </ThemeProvider>
  );
}